import { useAuth } from "@/contexts/AuthContext";
import { useTeamId } from "@/hooks/useTeamId";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

function getReadinessBand(score: number): { label: string; message: string } {
  if (score >= 70) return { label: "Strong Readiness", message: "You're well-positioned. Keep refining your plan." };
  if (score >= 50) return { label: "Moderate Readiness", message: "You're making progress. Address key gaps to stay on track." };
  return { label: "Early Stage", message: "Complete your assessments to improve your readiness." };
}

interface ScoreTileContentProps {
  onViewDetails?: () => void;
}

export function ScoreTileContent({ onViewDetails }: ScoreTileContentProps) {
  const { user } = useAuth();
  const { effectiveTeamId, loading: teamLoading } = useTeamId();

  const { data, isLoading } = useQuery({
    queryKey: ["userScores", user?.id, effectiveTeamId],
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from("user_scores")
        .select("score_value, updated_at")
        .eq("user_id", user!.id)
        .eq("entity_type", "overall")
        .order("updated_at", { ascending: false })
        .limit(2);

      if (error) throw error;
      return rows ?? [];
    },
    staleTime: 30_000,
    enabled: !!user?.id && !teamLoading,
  });

  const current = data && data.length > 0 ? data[0].score_value : null;
  const score = current != null ? Math.round(current) : 0;
  const band = getReadinessBand(score);
  const hasScore = current != null;

  return (
    <div className="flex flex-col items-start w-full h-full">
      <span className="text-[10px] uppercase tracking-widest text-white/60 font-semibold">
        mySuccession Readiness
      </span>

      <span className="text-5xl font-bold text-white mt-5">
        {hasScore ? `${score}%` : "—"}
      </span>

      <span className="text-xs text-white/70 mt-1">mySuccession Score</span>

      <span className="mt-3 inline-block rounded-full bg-white/20 px-3 py-0.5 text-[11px] font-medium text-white">
        {hasScore ? band.label : "Not Yet Scored"}
      </span>

      <p className="text-xs text-white/70 mt-4 leading-relaxed">
        {hasScore ? band.message : "Complete your first assessment to see your score."}
      </p>

      <div className="flex-1" />

      <Button
        variant="outline"
        size="sm"
        onClick={(e) => { e.stopPropagation(); onViewDetails?.(); }}
        className="mt-3 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white rounded-lg text-xs"
      >
        View Score Details
      </Button>
    </div>
  );
}
